import { useState, useEffect } from 'react';
import { X, Check } from 'lucide-react';
import ProfileRow from './ProfileRow';
import UserSearch from './UserSearch';
import useProfileData from '../hooks/useProfileData';

const TAG = '[UserSelector]';

/**
 * UserSelector - Component for selecting a user with search and display
 * 
 * Shows a search field initially. When a user is selected, displays them in a
 * rounded container with a ProfileRow and a remove button. When removed, shows
 * the search field again.
 *
 * @param {string} selectedUserId - The currently selected user ID (optional)
 * @param {function} onUserSelected - Callback when a user is selected (user) => void
 * @param {function} onUserRemoved - Callback when a user is removed () => void
 * @param {string} placeholder - Placeholder text for search input
 * @param {string} containerClassName - Optional className for the container
 */
function UserSelector({ selectedUserId, onUserSelected, onUserRemoved, placeholder = 'Search @tag or name...', containerClassName = '' }) {
  const { profile: selectedUser } = useProfileData(selectedUserId);

  const handleUserSelect = (user) => {
    if (onUserSelected) {
      onUserSelected(user);
    }
  };

  const handleRemove = () => {
    if (onUserRemoved) {
      onUserRemoved();
    }
  };

  // If a user is selected (ID exists), show the display row
  if (selectedUserId) {
    return (
      <div className={`rounded-xl border border-border bg-card p-3 ${containerClassName}`}>
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            {selectedUser ? (
              <ProfileRow
                profile={selectedUser}
                size="md"
              />
            ) : (
              <div className="text-sm text-muted-foreground">Loading...</div>
            )}
          </div>
          <button
            onClick={handleRemove}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-muted hover:bg-muted/80 transition-colors"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
      </div>
    );
  }

  // Otherwise, show the search field with a select button
  return (
    <UserSearch
      onSelected={handleUserSelect}
      rowButtons={[{
        icon: Check,
        label: 'Select',
        onClick: handleUserSelect
      }]}
      placeholder={placeholder}
      containerClassName={containerClassName}
    />
  );
}

export default UserSelector;
